import React, { memo } from "react"
import { graphql, useStaticQuery } from "gatsby"
import { Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, Flex, Heading, Text, VStack } from "@chakra-ui/react"

import { useGiftcardBalance } from "@app/hooks/useGiftcard"
import { FormButton } from "@app/components/Form/FormButton"
import { FormErrors } from "@app/components/Form/FormErrors"
import { FormInput } from "@app/components/Form/FormInput"
import { Icon } from "@app/components/Icon"
import { useGlobalContext } from "@app/providers/global"
import { ProductGiftcardBalanceTransactions } from "@app/components/Product/Giftcard/Balance/ProductGiftcardBalanceTransactions"

type Props = {
  handleToggle: () => void
  isOpen: boolean
}

export const ProductGiftcardBalanceForm: React.FC<Props> = memo(({ handleToggle, isOpen }) => {
  const { additionalClose } = useGlobalContext()
  const { giftcard } = useStaticQuery<GatsbyTypes.ProductGiftcardBalanceFormQuery>(graphql`
    query ProductGiftcardBalanceForm {
      giftcard: sanityPageGiftcard {
        additionalGiftcardTitle
        additionalGiftcardDesc
        additionalGiftcardBalance
        additionalGiftcardCheck
        additionalGiftcardExpiry
        additionalGiftcardNumber
        additionalGiftcardPin
        additionalGiftcardSubmit
        additionalGiftcardInvalidCard
        additionalGiftcardInvalidField
      }
    }
  `)

  const { balance, transactions, data, errors, handleChange, handleSubmit, loading } = useGiftcardBalance()

  return (
    <Drawer isOpen={isOpen} placement="right" size="xl" onClose={handleToggle} returnFocusOnClose={false}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader
          as={VStack}
          bg="white"
          px={[7.5, 5.5, 12]}
          pt={[7.5, 5.5, 11]}
          pb={[2, 2.5, 3]}
          flexDirection="row"
          justifyContent="space-between"
          gap={{ base: 4, sm: 2 }}
          alignItems="flex-start"
        >
          <Flex alignItems="left" justifyContent="center">
            <Heading
              as="h3"
              color="grey.coal"
              fontWeight={500}
              letterSpacing="generous"
              w="full"
              lineHeight={1.25}
              mt={[2, 4.75]}
              mb={3}
              textAlign="left"
              textTransform="initial"
              fontSize="3xl"
            >
              {giftcard?.additionalGiftcardTitle}
            </Heading>
          </Flex>
          <Flex alignItems="baseline" justifyContent="flex-end">
            <FormButton onClick={handleToggle} title={additionalClose} aria-label={additionalClose} p={2} variant="ghost">
              <Icon name="close" width={5} />
            </FormButton>
          </Flex>
        </DrawerHeader>
        <DrawerBody
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="flex-start"
          bg="white"
          py={0}
          px={[7.5, 24.25, 12.5]}
        >
          <VStack alignItems="left" justifyContent="flex-start" spacing={{ base: 5, sm: 10 }} w="full" pb="6">
            <Flex alignItems="left" justifyContent="left">
              <Text size="md" letterSpacing="generous" w="full" color="grey.coal">
                {giftcard?.additionalGiftcardDesc}
              </Text>
            </Flex>
            <VStack as="form" alignItems="stretch" justifyContent="flex-start" noValidate onSubmit={handleSubmit} spacing={6} w="full">
              <FormInput
                autoComplete="off"
                autoFocus
                containerProps={{ pt: 1 }}
                borderRadius="8px"
                errors={
                  errors.includes("voucher")
                    ? [`${giftcard?.additionalGiftcardInvalidField?.replace("{field}", giftcard?.additionalGiftcardNumber || "")}`]
                    : []
                }
                errorAlignment="left"
                errorIcon={false}
                isRequired
                name="voucher"
                onChange={handleChange}
                placeholder={giftcard?.additionalGiftcardNumber}
                type="text"
                borderColor={errors.length < 1 ? "grey.mid" : "var(--chakra-colors-brand-error) !important"}
                value={data?.voucher}
                labelColor={errors.length < 1 ? "grey.coal" : "brand.error"}
              />
              <FormInput
                autoComplete="off"
                errors={
                  errors.includes("pin")
                    ? [`${giftcard?.additionalGiftcardInvalidField?.replace("{field}", giftcard?.additionalGiftcardPin || "")}`]
                    : []
                }
                errorAlignment="left"
                errorIcon={false}
                isRequired
                name="pin"
                onChange={handleChange}
                placeholder={giftcard?.additionalGiftcardPin}
                type="text"
                value={data?.pin}
                borderRadius="8px"
                borderColor={errors.length < 1 ? "grey.mid" : "var(--chakra-colors-brand-error) !important"}
                labelColor={errors.length < 1 ? "grey.coal" : "brand.error"}
              />
              <FormButton
                isDisabled={loading}
                isLoading={loading}
                size="md"
                title={giftcard?.additionalGiftcardSubmit}
                arial-label={giftcard?.additionalGiftcardSubmit}
                type="submit"
                variant="pdpAddToCart"
                borderRadius="8px"
              >
                {giftcard?.additionalGiftcardSubmit}
              </FormButton>
              {errors?.filter(error => !error.includes("pin") && !error.includes("voucher"))?.length > 0 && (
                <VStack alignItems="center" justifyContent="flex-start" w="full">
                  <FormErrors
                    errors={errors?.map(error =>
                      error
                        .replace("Cert not exist", giftcard?.additionalGiftcardInvalidCard || "")
                        .replace(
                          "Invalid security code",
                          giftcard?.additionalGiftcardInvalidField?.replace("{field}", giftcard?.additionalGiftcardPin || "")
                        )
                    )}
                    direction="column"
                    justifyContent="center"
                    textAlign="center"
                    mt={2}
                    maxW={["unset", 230]}
                    sx={{ svg: { mb: 2 } }}
                  />
                </VStack>
              )}
            </VStack>
            {balance?.amount && balance?.date && (
              <VStack alignItems="center" justifyContent="flex-start" spacing={2} w="full" mb={15}>
                <Text color="grey.coal" size="md">
                  {giftcard?.additionalGiftcardBalance}
                </Text>
                <Text color="grey.coal" letterSpacing="generous" lineHeight={1.5} size="xl">
                  {balance.amount}
                </Text>
                <Text color="grey.mid" letterSpacing="generous" pt={3} size="xs">
                  {giftcard?.additionalGiftcardExpiry?.replace("{date}", balance.date)}
                </Text>
              </VStack>
            )}
          </VStack>
          {!!transactions?.length && <ProductGiftcardBalanceTransactions transactions={transactions} />}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
})
